import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { BulkAction } from "@itandi/itandi-bb-ui/BulkAction";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import { useCallback, useState } from "react";
import useSWRMutation from "swr/mutation";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientSettingLayout } from "~/components/common/layouts/settings/ClientSettingLayout";
import { useCompanyStoreMails } from "~/hooks/useCompanyStoreMails";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy as destroyMails } from "~/utils/api/CompanyStoreMails";
import { generatePath } from "~/utils/generatePath";
import { CompanyStoreMailModal } from "./CompanyStoreMailModal";

export function Page() {
	const { notify } = useNotification();
	const [checkNumbers, setCheckNumbers] = useState<number[]>([]);
	const { data: companyMails } = useCompanyStoreMails().index();
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const handleOpenConfirmModal = useCallback(() => {
		setIsConfirmModalOpen(true);
	}, []);
	const handleCloseConfirmModal = useCallback(() => {
		setIsConfirmModalOpen(false);
	}, []);

	const { trigger: triggerDestroy, isMutating } = useSWRMutation(
		generatePath("/company_store_mails"),
		async (_key, { arg }: { arg: number[] }) => destroyMails(arg)
	);

	const handleDestroyMails = useCallback(async () => {
		const result = await triggerDestroy(checkNumbers);
		if (isSuccess(result)) {
			handleCloseConfirmModal();
			setCheckNumbers([]);
			notify({ content: "削除しました。", variant: "success" });
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	}, [checkNumbers, handleCloseConfirmModal, notify, triggerDestroy]);

	const handleCheckNumbers = useCallback(
		(deleteIds: number) => {
			if (checkNumbers.includes(deleteIds)) {
				setCheckNumbers(checkNumbers.filter((num) => num !== deleteIds));
			} else {
				setCheckNumbers([...checkNumbers, deleteIds]);
			}
		},
		[checkNumbers]
	);

	return (
		<DefaultV2>
			<ClientSettingLayout>
				<Panel>
					<PanelHeader
						rightArea={
							<Button
								onClick={() => {
									setIsCreateModalOpen(true);
								}}
								variant="primary"
							>
								追加
							</Button>
						}
						title="通知先メールアドレス設定"
					/>
					<PanelBody>
						<BulkAction
							actionsArea={
								<Button
									height="small"
									onClick={handleOpenConfirmModal}
									variant="danger"
								>
									削除
								</Button>
							}
							selectedCount={checkNumbers.length}
						>
							<Table>
								<Thead>
									<Tr>
										<Th width={"2rem"} />
										<Th>メールアドレス</Th>
									</Tr>
								</Thead>
								<Tbody>
									{companyMails?.map((companyMail) => (
										<Tr key={companyMail.id}>
											<Td>
												<InputCheckBox
													checked={checkNumbers.includes(companyMail.id)}
													onChange={(): void => {
														handleCheckNumbers(companyMail.id);
													}}
												/>
											</Td>
											<Td>{companyMail.email}</Td>
										</Tr>
									))}
								</Tbody>
							</Table>
						</BulkAction>
					</PanelBody>
				</Panel>
				{isConfirmModalOpen ? (
					<Modalv2 isOpen onClose={handleCloseConfirmModal} size="s">
						<ModalHeader title="メールアドレスの一括削除" />
						<ModalBody>
							{`${checkNumbers.length}件のメールアドレスを削除します。`}
							<Alert mt={designTokens["spacing/m"]} variant="warning">
								この操作は元に戻せません。
							</Alert>
						</ModalBody>
						<ModalFooter
							leftArea={
								<Button
									disabled={isMutating}
									onClick={handleCloseConfirmModal}
									variant="secondary"
								>
									キャンセル
								</Button>
							}
							rightArea={
								<Button
									loading={isMutating}
									onClick={handleDestroyMails}
									variant="danger"
								>
									削除
								</Button>
							}
						/>
					</Modalv2>
				) : null}
				{isCreateModalOpen ? (
					<CompanyStoreMailModal
						onClose={() => {
							setIsCreateModalOpen(false);
						}}
					/>
				) : null}
			</ClientSettingLayout>
		</DefaultV2>
	);
}
